<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { FormDefinition, FormResponse, Workflow } from "~/api";
import FormRendererInner from "~/NewFormBuilder/components/Renderer/FormRendererInner.vue";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  formGuid: string;
  responseGuid?: string;
  workflow?: Workflow;
  incomingDisplayMode?: FormDisplayMode;
  pdfViewer?: boolean;
  response?: FormResponse;
  /** True when the parent has determined this response is a superseded (previous) version. */
  isPreviousVersion?: boolean;
  /** Answers from all forms that are marked for sharing */
  sharedAnswers?: Record<string, string>;
  /** Optional callback to trigger workflow refresh after status changes */
  onWorkflowRefresh?: () => Promise<void>;
}>();

const myInfoStore = useMyInfoStore();

const formResponse = ref<FormResponse>();
const form = ref<FormDefinition>();
// const complianceItem = ref<ItemDefinition>();
const formText = ref<string>(JSON.stringify(form, null, 2));
const loading = ref<boolean>(true);
const displayMode = ref<FormDisplayMode>(
  props.incomingDisplayMode || FormDisplayMode.EDIT,
);

// Keep display mode in sync when the parent's gating changes after mount
// (e.g. a status change flips a form to read-only while it is rendered)
watch(
  () => props.incomingDisplayMode,
  (mode) => {
    displayMode.value = mode ?? FormDisplayMode.EDIT;
  },
);

onMounted(async () => {
  await Promise.all([myInfoStore.load()]);

  const responseGuid = props.responseGuid;

  if (!responseGuid) {
    loading.value = false;
    showError("Unable to load form response: No responseGuid provided.");
    return;
  }

  if (!props.response) {
    await apiClient.formResponse
      .getFormResponse(
        myInfoStore.currentSysOrgGuid,
        props.formGuid,
        responseGuid,
      )
      .then((fr) => {
        loading.value = false;
        formResponse.value = fr;
        form.value = fr.formDefinition;
        formText.value = JSON.stringify(form.value, null, 2);
        displayMode.value = props.incomingDisplayMode ?? FormDisplayMode.EDIT;
      })
      .catch(showError);
  } else {
    formResponse.value = props.response;
    form.value = props.response.formDefinition;
    formText.value = JSON.stringify(form.value, null, 2);
    displayMode.value = props.incomingDisplayMode ?? FormDisplayMode.EDIT;
    loading.value = false;
  }
});
</script>

<template>
  <div v-loading="loading">
    <div v-if="form" class="complianceFormRendererPage">
      <!-- slot for extra info -->
      <slot name="additional-info"></slot>

      <FormRendererInner
        v-model="form"
        :form-response="formResponse"
        :display-mode="displayMode"
        :workflow="workflow"
        :pdf-viewer="props.pdfViewer"
        :is-previous-version="props.isPreviousVersion"
        :shared-answers="sharedAnswers"
        :on-workflow-refresh="props.onWorkflowRefresh"
      />
    </div>
  </div>
</template>

<style lang="less">
.complianceFormRendererPage {
  display: grid;
  margin-top: 1em;

  .compliance-info {
    table {
      width: 50%;
      td {
        padding: 0.5em;
        border-bottom: 1px solid #cbced5;
      }
    }
  }

  .formContainer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 1200px;
  }

  .formAnswers {
    padding: 4px 8px;
    white-space: break-spaces;
  }

  .form {
    box-shadow: #82675336 0 0 8px 4px;
    border: 0px;
  }
}
</style>
